/**
 * translate-server-lane.js — «Точный перевод»: браузерная половина серверного
 * Премиум-лейна /document-translate и /photo-translate.
 *
 * Один модуль на оба тула, потому что контракт у них один (App\Http\Handlers\
 * DocumentTranslateHandler::translateUnits): все абзацы или все блоки снимка
 * уходят ОДНИМ запросом, назад приходят переводы по тем же индексам.
 *
 * ⚠️ Число переводов обязано совпасть с числом абзацев — и это проверяется ЗДЕСЬ,
 * а не только на сервере. Ответ другой длины не «почти годный»: принять его
 * значит поставить перевод пятого абзаца на место четвёртого и далее по всему
 * документу. Такой ответ — отказ, и тул переводит обычным лейном.
 *
 * ⚠️ `X-Requested-With` и `redirect:'error'` обязательны. Без первого отказ
 * лейна приходит РЕДИРЕКТОМ на страницу, fetch молча по нему идёт, и HTML
 * разбирается как ответ (замер на /recording-studio, 25.08).
 *
 * ⚠️ Имя `serverUrl` в `fetch(o.serverUrl …)` — не случайное: по нему
 * ServerLaneToolsDocumentedTest прослеживает лейн от маршрута в блейде до этого
 * вызова. Переименовать поле — значит сделать лейн невидимым детектору, и при
 * флипе в public он уехал бы в теневой backstop неметеренным.
 */
(function () {
    'use strict';

    function csrf() {
        var m = document.querySelector('meta[name="csrf-token"]');
        return m ? m.getAttribute('content') || '' : '';
    }

    /*
     * Заказ за токены переживает обрыв (App\Services\Tokens\PaidOrder).
     * Точный перевод части идёт 40 с и дольше; 11.10.2026 вкладка Android
     * замерзала посреди ответа, сервер переводил и списывал, а страница
     * получала «Failed to fetch», переводила обычным лейном и на повторе
     * слышала «не хватает токенов» — их держал брошенный запрос. Теперь у
     * запроса есть ключ, и ответ, который не дошёл, забирается по нему
     * (`statusUrl`) — без второго перевода и второго списания. Тул без
     * `statusUrl` (/photo-translate) работает как раньше.
     */
    var COLLECT_MS = 5 * 60 * 1000;

    function newRid() {
        try {
            if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID().replace(/-/g, '');
        } catch (e) { /* старый браузер — ниже */ }
        var s = '';
        while (s.length < 24) s += Math.random().toString(36).slice(2);
        return s.slice(0, 24);
    }

    function pause(ms, signal) {
        return new Promise(function (resolve, reject) {
            var t = setTimeout(resolve, ms);
            if (signal) {
                signal.addEventListener('abort', function () {
                    clearTimeout(t);
                    reject(new DOMException('Aborted', 'AbortError'));
                }, { once: true });
            }
        });
    }

    function funnel(stage, data) {
        try { if (window.__aeFunnel) window.__aeFunnel(stage, data || {}); } catch (e) { /* телеметрия не мешает переводу */ }
    }

    /** Переводы ответа → результат; ответ другой длины — отказ (см. шапку). */
    function accept(o, data) {
        if (!Array.isArray(data.texts) || data.texts.length !== o.texts.length) {
            throw { status: 502, message: '', premium: false };
        }
        return {
            texts: data.texts.map(function (x) { return String(x == null ? '' : x); }),
            failed: Array.isArray(data.failed) ? data.failed.filter(function (i) { return Number.isInteger(i); }) : [],
            // Лейн за токены говорит, сколько списано и что осталось.
            charged: data.charged | 0,
            balance: typeof data.balance === 'number' ? data.balance : null,
        };
    }

    /**
     * Ответ не дошёл — спросить заказ по ключу. 202 — ещё переводится, 200 —
     * тот же перевод, 404 — заказа нет (не доходил или провалился, токены не
     * списаны). Обрыв самого вопроса — не ответ: спрашиваем снова до срока.
     */
    async function collect(o, rid) {
        var url = String(o.statusUrl).split('RIDPLACEHOLDER').join(encodeURIComponent(rid));
        var started = Date.now();
        var tries = 0;
        while (Date.now() - started < COLLECT_MS) {
            if (tries) await pause(tries < 3 ? 2000 : 4000, o.signal);
            tries++;
            var r;
            try {
                r = await fetch(url, {
                    headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                    credentials: 'same-origin', redirect: 'error', signal: o.signal,
                });
            } catch (e) {
                if (e && e.name === 'AbortError') throw e;
                continue;
            }
            if (r.status === 404 || r.status === 401) {
                funnel('lane_order_lost', { why: r.status === 404 ? 'unknown' : 'signed_out', tries: tries });
                throw { status: 0, message: '', premium: false };
            }
            if (!r.ok || r.status === 202) continue;
            var data = null;
            try { data = await r.json(); } catch (e) { continue; }
            funnel('lane_order_recovered', { tries: tries, wait_s: Math.round((Date.now() - started) / 1000) });
            return accept(o, data);
        }
        funnel('lane_order_lost', { why: 'timeout', tries: tries });
        throw { status: 0, message: '', premium: false };
    }

    /**
     * Перевести набор текстов одним запросом.
     *
     * @param {{serverUrl:string, texts:string[], src:string, tgt:string,
     *          terms?:string[], style?:{formality?:string,domain?:string},
     *          signal?:AbortSignal}} o
     * @returns {Promise<{texts:string[], failed:number[]}>}
     * @throws {{status:number, message:string, premium:boolean,
     *           maxChars?:number, chars?:number}}  (AbortError пробрасывается как есть)
     */
    async function translate(o) {
        var rid = o.statusUrl ? newRid() : null;
        var res;
        try {
            res = await fetch(o.serverUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-CSRF-TOKEN': csrf(),
                },
                credentials: 'same-origin',
                redirect: 'error',
                signal: o.signal,
                body: JSON.stringify({
                    texts: o.texts,
                    src: o.src || 'auto',
                    tgt: o.tgt,
                    terms: o.terms || [],
                    // { formality?: 'formal'|'informal', domain?: string } —
                    // читает только модель, и только она их и может прочесть:
                    // ни встроенный переводчик браузера, ни публичные HTTP-
                    // провайдеры бесплатного лейна таких полей не принимают.
                    // Пустой объект сервер игнорирует.
                    style: o.style || {},
                    rid: rid || undefined,
                    // Согласие запустить за токены (public/js/lane-pay.js): лейн с
                    // ценой тратит свои запуски Премиума первыми.
                    pay: o.pay ? 'tokens' : undefined,
                }),
            });
        } catch (e) {
            if (e && e.name === 'AbortError') throw e;
            // Сеть отказала до ответа — часть, возможно, уже переводится и
            // будет оплачена: забираем её по ключу, а не объявляем провал.
            if (rid) return collect(o, rid);
            throw { status: 0, message: '', premium: false };
        }
        // 202 — та же часть уже идёт (повтор нажатия): ждём её же.
        if (rid && res.status === 202) return collect(o, rid);

        var data = null;
        try {
            data = await res.json();
        } catch (e) {
            data = null;
        }
        // Не JSON при 5xx — ответил край (502/504/524), а не лейн: судьба
        // части неизвестна, спрашиваем заказ.
        if (rid && data === null && res.status >= 500) return collect(o, rid);

        if (!res.ok || !data || data.success === false) {
            // 402 бывает двух видов: у /photo-translate — закрытая дверь Премиума,
            // у /document-translate (с 09.10.2026) — отказ кошелька: токенов не
            // хватает или человек не вошёл. Второй несёт числа для стены покупки.
            var tokens = (data && data.insufficient_tokens) ? {
                need: data.need | 0, balance: data.balance | 0, short: data.short | 0,
                guest: !!data.guest, reason: data.reason || '', msg: data.msg || '',
            } : null;
            throw {
                status: res.status,
                message: (data && typeof data.error === 'string' && data.error !== 'insufficient_tokens') ? data.error : '',
                premium: res.status === 402 && !tokens,
                tokens: tokens,
                maxChars: data && data.max_chars,
                chars: data && data.chars,
                // Ответ целиком: в нём цена запуска за токены (`pay`), если есть.
                data: data,
            };
        }

        var out = accept(o, data);
        if (data && data.paid_tokens > 0) { out.paid_tokens = data.paid_tokens; out.paid_text = String(data.paid_text || ''); }
        return out;
    }

    window.__translateLane = { translate: translate };
})();
